<template>
  <v-dialog v-model="isModalOpen" max-width="600px" persistent>
    <v-card flat>
      <!-- Modal Header -->
      <v-card-title class="modal-header d-flex align-center flex-wrap">
        <span class="headline flex-grow-1" style="white-space: normal; word-break: break-word;">
          Get Your Copy of the Floor Plan
        </span>
        <v-btn icon @click="closeModal" variant="outlined" x-small class="flex-shrink-0 close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Modal Form -->
      <v-card-text>
        <v-form ref="form">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="formData.name"
                label="Full Name"
                :rules="nameRules"
                required
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="formData.email"
                label="Email Address"
                :rules="emailRules"
                required
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="formData.phone"
                label="Phone Number"
                :rules="phoneRules"
                required
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="formData.message"
                label="Message"
                :rules="messageRules"
                outlined
                dense
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-btn @click="submitForm" color="primary" block :loading="isSubmitting" :disabled="!isFormValid || isSubmitting">
                Submit
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
/* global gtag */

export default {
  name: 'ModalForm',
  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isModalOpen: this.isOpen,
      isSubmitting: false,
      formData: {
        name: '',
        email: '',
        phone: '',
        message: '',
      },
      snackbar: {
        visible: false,
        message: '',
        color: 'green',
      },
      nameRules: [(v) => !!v || 'Name is required'],
      emailRules: [
        (v) => !!v || 'Email is required',
        (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
      ],
      phoneRules: [
        (v) => !!v || 'Phone number is required',
        (v) => /^[89]\d{7}$/.test(v) || 'Phone number must be 8 digits starting with 8 or 9',
      ],
      messageRules: [(v) => !!v || 'Message is required'],
    };
  },
  computed: {
    isFormValid() {
      return (
        this.nameRules?.every((rule) => rule(this.formData.name) === true) &&
        this.emailRules?.every((rule) => rule(this.formData.email) === true) &&
        this.phoneRules?.every((rule) => rule(this.formData.phone) === true) &&
        this.messageRules?.every((rule) => rule(this.formData.message) === true)
      );
    },
  },
  watch: {
    isOpen(newVal) {
      this.isModalOpen = newVal;
    },
  },
  methods: {
    closeModal() {
      this.isModalOpen = false;
      this.$emit('close');
    },
    async submitForm() {
      if (!this.isFormValid) return;
      this.isSubmitting = true;

      const payload = {
        project_id: 'arina-east',
        time: new Date(),
        name: this.formData.name.trim(),
        email: this.formData.email.toLowerCase().trim(),
        phone: this.formData.phone.trim(),
        message: this.formData.message.trim(),
        query_type: []
      };

      try {
        const response = await fetch('https://asia-southeast1-stones-9cc17.cloudfunctions.net/saveContact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          // Send Google Tag event
          gtag('event', 'conversion', {
              'send_to': 'AW-16841310084/3GyCCIK_v6QaEIT_x94-',
              'value': 1.0,
              'currency': 'SGD'
          });

          this.snackbar.message = 'Your enquiry has been submitted successfully!';
          this.snackbar.color = 'green';
          this.resetForm();
          this.closeModal();
        } else {
          this.snackbar.message = 'Error submitting your enquiry. Please try again.';
          this.snackbar.color = 'red';
        }
      } catch (error) {
        console.error('API Error:', error);
        this.snackbar.message = 'Network error. Please check your connection.';
        this.snackbar.color = 'red';
      } finally {
        this.snackbar.visible = true;
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.formData.name = '';
      this.formData.email = '';
      this.formData.phone = '';
      this.formData.message = '';
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },
  },
};
</script>

<style scoped>
.modal-header {
  background-color: #343742;
  color: #F7F4ED;
  padding: 16px;
  gap: 8px; /* Adds spacing between headline and close button */
}

.headline {
  font-size: 1.5rem;
  font-weight: bold;
  word-break: break-word; /* Ensures long text wraps to the next line */
}

/* Custom close button styles */
.close-btn {
  width: 24px; /* Adjust as needed */
  height: 24px; /* Adjust as needed */
}

.close-btn .v-icon {
  font-size: 14px; /* Custom font size for the close icon */
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .headline {
    font-size: 1.25rem;
  }
}
</style>